import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  audioMensajes,
  scoreAccionesAudio,
  reglasAudio,
  globalStatsAudio,
  usoLabels,
  estadoModeracionLabels,
  duracionLabels,
  getMasEscuchados,
  getMasCompartidos,
  getMayorScore,
  getAudiosPorEstado,
} from '@/mocks/audioMensajes';
import type { UsoAudio, DuracionAudio, EstadoModeracion } from '@/mocks/audioMensajes';

type TabAudios = 'destacados' | 'mas-escuchados' | 'mas-compartidos' | 'acciones' | 'reglas';

export default function AudioMensajesSection() {
  const [activeTab, setActiveTab] = useState<TabAudios>('destacados');
  const [usoFiltro, setUsoFiltro] = useState<UsoAudio | 'todos'>('todos');
  const [duracionFiltro, setDuracionFiltro] = useState<DuracionAudio | 'todos'>('todos');

  const destacados = getAudiosPorEstado('destacado');
  const masEscuchados = getMasEscuchados().slice(0, 6);
  const masCompartidos = getMasCompartidos().slice(0, 6);

  const usoPills: { uso: UsoAudio | 'todos'; label: string; icono: string }[] = [
    { uso: 'todos', label: 'Todos', icono: 'ri-filter-line' },
    { uso: 'saludo', label: 'Saludos', icono: 'ri-message-3-line' },
    { uso: 'concurso', label: 'Concursos', icono: 'ri-trophy-line' },
    { uso: 'participacion', label: 'Participación', icono: 'ri-chat-smile-2-line' },
    { uso: 'mensaje', label: 'Mensajes', icono: 'ri-mail-line' },
  ];

  const duracionPills: { duracion: DuracionAudio | 'todos'; label: string }[] = [
    { duracion: 'todos', label: 'Todas' },
    { duracion: '30', label: '30s (Básico)' },
    { duracion: '60', label: '60s (Pro)' },
    { duracion: '90', label: '90s (Premium)' },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse"></span>
            AUDIO MENSAJES
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Grabá, Participá y Hacete Escuchar
          </h2>
          <p className="text-foreground-500 text-sm max-w-[620px] mx-auto leading-relaxed">
            Mandá audio mensajes de 30, 60 o 90 segundos según tu plan. Para saludos, concursos, participación y mensajes. Con moderación y la regla de oro: nunca se reproducen automáticamente.
          </p>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Audios Hoy', valor: globalStatsAudio.audiosHoy.toString(), icono: 'ri-mic-line', color: 'text-violet-600', bg: 'bg-violet-100' },
            { label: 'Aprobados', valor: globalStatsAudio.audiosAprobados.toString(), icono: 'ri-check-line', color: 'text-emerald-600', bg: 'bg-emerald-100' },
            { label: 'Destacados', valor: globalStatsAudio.audiosDestacados.toString(), icono: 'ri-star-line', color: 'text-amber-600', bg: 'bg-amber-100' },
            { label: 'Score Generado', valor: globalStatsAudio.scoreTotalGenerado.toLocaleString(), icono: 'ri-flashlight-line', color: 'text-rose-600', bg: 'bg-rose-100' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${stat.icono} ${stat.color} text-sm`}></i>
              </div>
              <p className="text-foreground-950 font-bold text-lg">{stat.valor}</p>
              <p className="text-foreground-400 text-[10px]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1.5 mb-4 flex-wrap">
          {([
            { id: 'destacados', icono: 'ri-star-line', label: 'Destacados' },
            { id: 'mas-escuchados', icono: 'ri-headphone-line', label: 'Más Escuchados' },
            { id: 'mas-compartidos', icono: 'ri-share-forward-line', label: 'Más Compartidos' },
            { id: 'acciones', icono: 'ri-flashlight-line', label: 'Score' },
            { id: 'reglas', icono: 'ri-scales-line', label: 'Reglas' },
          ] as { id: TabAudios; icono: string; label: string }[]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
              }`}
            >
              <i className={tab.icono}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'acciones' ? (
          <div className="max-w-[700px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {scoreAccionesAudio.map((acc) => (
                <div key={acc.id} className="bg-background-50 rounded-2xl border border-background-200 p-5 text-center">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${acc.color} flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${acc.icono} text-background-50 text-lg`}></i>
                  </div>
                  <p className="text-foreground-950 font-bold text-sm mb-1">{acc.accion}</p>
                  <p className="text-foreground-400 text-xs leading-relaxed mb-3">{acc.descripcion}</p>
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-bold">
                    <i className="ri-add-line"></i>+{acc.puntos} pts
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : activeTab === 'reglas' ? (
          <div className="max-w-[900px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reglasAudio.map((regla) => (
                <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                      <i className={`${regla.icono} text-background-50 text-sm`}></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-950 font-bold text-sm mb-1">{regla.titulo}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Audios grid */
          <div>
            {/* Filters */}
            <div className="flex items-center justify-center gap-2 mb-5 flex-wrap">
              <div className="flex items-center gap-1">
                {usoPills.map((pill) => (
                  <button
                    key={pill.uso}
                    onClick={() => setUsoFiltro(pill.uso)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      usoFiltro === pill.uso
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    <i className={`${pill.icono} text-[10px]`}></i>
                    {pill.label}
                  </button>
                ))}
              </div>
              <span className="text-foreground-300 text-[10px]">|</span>
              <div className="flex items-center gap-1">
                {duracionPills.map((pill) => (
                  <button
                    key={pill.duracion}
                    onClick={() => setDuracionFiltro(pill.duracion)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      duracionFiltro === pill.duracion
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(activeTab === 'destacados' ? destacados : activeTab === 'mas-escuchados' ? masEscuchados : masCompartidos)
                .filter((a) => {
                  if (usoFiltro !== 'todos' && a.uso !== usoFiltro) return false;
                  if (duracionFiltro !== 'todos' && a.duracion !== duracionFiltro) return false;
                  return true;
                })
                .slice(0, 6)
                .map((a) => {
                  const usoInfo = usoLabels[a.uso];
                  const estadoInfo = estadoModeracionLabels[a.estado];
                  const durInfo = duracionLabels[a.duracion];
                  return (
                    <div key={a.id} className="bg-background-50 rounded-2xl border border-background-200 p-4 hover:border-background-300 transition-colors">
                      {/* Uso + Estado + Duración */}
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-1.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${usoInfo.color} text-[9px] font-semibold`}>
                          <i className={`${usoInfo.icono} text-[9px]`}></i>
                          {usoInfo.label}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center gap-0.5 ${durInfo.bg} ${durInfo.color} px-2 py-0.5 rounded-md text-[9px] font-semibold`}>
                            <i className="ri-timer-line text-[9px]"></i>
                            {durInfo.label}
                          </span>
                          <span className="flex items-center gap-1 text-[9px]">
                            <span className={`w-1.5 h-1.5 rounded-full ${estadoInfo.dot}`}></span>
                            <span className="text-foreground-500">{estadoInfo.label}</span>
                          </span>
                        </div>
                      </div>

                      {/* Audio visualizer + player hint */}
                      <div className="flex items-center gap-3 mb-3 bg-violet-50 rounded-xl px-4 py-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shrink-0">
                          <i className="ri-mic-fill text-background-50 text-base"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-end gap-0.5 mb-1">
                            {[...Array(7)].map((_, i) => (
                              <span
                                key={i}
                                className="w-1 bg-violet-400 rounded-full"
                                style={{ height: `${4 + Math.random() * 12}px` }}
                              ></span>
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-violet-500 text-[10px]">{a.duracionSegundos}s</p>
                            <span className="text-violet-300 text-[9px]">— Clic para escuchar</span>
                          </div>
                        </div>
                      </div>

                      {/* Text */}
                      <p className="text-foreground-600 text-[11px] leading-relaxed line-clamp-3 mb-3">
                        &ldquo;{a.texto}&rdquo;
                      </p>

                      {/* Remitente */}
                      <div className="flex items-center gap-2 mb-2">
                        <img
                          src={a.remitenteAvatar}
                          alt={a.remitente}
                          className="w-5 h-5 rounded-full object-cover"
                          loading="lazy"
                        />
                        <span className="text-foreground-600 text-[10px] font-medium">{a.remitente}</span>
                        <span className="text-foreground-400 text-[9px]">{a.remitenteCiudad}</span>
                        <span className="text-foreground-300 text-[9px] ml-auto">{a.remitentePlan}</span>
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        <i className="ri-broadcast-line text-foreground-400 text-[9px]"></i>
                        <span className="text-foreground-400 text-[9px]">{a.destinoNombre}</span>
                      </div>

                      {/* Rejected reason */}
                      {a.estado === 'rechazado' && a.motivoRechazo && (
                        <div className="mb-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-100 text-[9px] text-rose-600 leading-relaxed">
                          <i className="ri-close-circle-line mr-1"></i>
                          {a.motivoRechazo}
                        </div>
                      )}

                      {/* Stats footer */}
                      <div className="flex items-center gap-3 pt-2 border-t border-background-100">
                        <span className="text-foreground-400 text-[9px] flex items-center gap-1">
                          <i className="ri-headphone-line"></i>{a.escuchas}
                        </span>
                        <span className="text-foreground-400 text-[9px] flex items-center gap-1">
                          <i className="ri-share-forward-line"></i>{a.compartidos}
                        </span>
                        <span className="text-emerald-700 text-[9px] font-semibold ml-auto flex items-center gap-0.5">
                          <i className="ri-flashlight-fill"></i>{a.score} pts
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/audio-mensajes"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-amber-500 text-background-50 font-bold text-sm cursor-pointer hover:from-violet-600 hover:to-amber-600 transition-all whitespace-nowrap"
          >
            <i className="ri-mic-line"></i>
            Ver Todos los Audio Mensajes
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-foreground-400 text-xs italic">
            &ldquo;Nunca reproducir automáticamente. Cada reproducción requiere un clic consciente del oyente o locutor.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}